import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useQuery,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { Box, Paper, CircularProgress } from "@mui/material";
import toast from "react-hot-toast";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import useZustandStore from "../../../zustandStore/useZustandStore";
import usePermissions from "../../../hooks/usePermissions";
import AdsAPIs from "../../../apiRoutes/Ads";
import AgencyAPIs from "../../../apiRoutes/Agency";
import {
  GET_AGENCIES_QUERY_KEY,
  GET_AD_REQUESTS_QUERY_KEY,
} from "../../../utils/constants/queryKeys";
import {
  PERMISSION_CODES,
  AGENCY_LIST_PERMISSIONS,
} from "../../../utils/constants/permissions";
import {
  agencyDisplayName,
  getAgencyIdFromUser,
  isAgencyScopedUser,
} from "../../../utils/agencyScope";
import {
  fetchAllFilteredRows,
  downloadExcelFromRows,
  exportFilename,
} from "../../../utils/exportFilteredListToExcel";
import PaginationFooter from "../../common/PaginationFooter";
import AssignDeliveryDateModal from "../Modals/AssignDeliveryDateModal";
import AssignSprintModal from "../Modals/AssignSprintModal";
import BulkLaunchDateModal from "../Modals/BulkLaunchDateModal";
import DeleteConfirmModal from "../Modals/DeleteConfirmModal";
import BulkDeleteByDateModal from "../Modals/BulkDeleteByDateModal";
import EditAdRequestModal from "../Modals/EditAdRequestModal";
import LaunchStatusModal from "../Modals/LaunchStatusModal";
import ModerationStatusModal from "../Modals/ModerationStatusModal";
import RequestFilterBar from "./RequestFilterBar";
import RequestListActions from "./RequestListActions";
import RequestsTable from "./RequestsTable";
import SelectionBar from "./SelectionBar";
import { adRequestExportColumns } from "../../../utils/constants/AdRequest";
import useUrlPaging from "../../../hooks/useUrlPaging";

const AdRequestList = ({ view }) => {
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();
  const { hasPermission, isSuperAdmin } = usePermissions();
  const canMutateRequests = hasPermission(PERMISSION_CODES.AD_REQUEST_UPDATE);
  const canListAgencies = AGENCY_LIST_PERMISSIONS.some((p) => hasPermission(p));
  const currentUser = useZustandStore((s) => s?.currentUser);
  const defaultAgencyId = getAgencyIdFromUser(currentUser);
  const agencyScoped = isAgencyScopedUser(currentUser);
  const canEditLaunchDate = canMutateRequests && (!agencyScoped || isSuperAdmin);
  const showAgencyFilter = defaultAgencyId == null && canListAgencies;
  const [launchModal, setLaunchModal] = useState(null);
  const [moderationModal, setModerationModal] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deliveryDateModal, setDeliveryDateModal] = useState({ open: false });
  const [sprintModal, setSprintModal] = useState({ open: false });
  const [launchDateModal, setLaunchDateModal] = useState({ open: false });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [bulkDeleteByDateOpen, setBulkDeleteByDateOpen] = useState(false);
  const [editRequest, setEditRequest] = useState(null);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const openDetail = (id) => navigate(`/dashboard/ad-request/${id}`);

  const { data: agenciesResult } = useQuery({
    queryKey: GET_AGENCIES_QUERY_KEY,
    queryFn: () =>
      AgencyAPIs.list({
        axiosPrivate,
        status: "approved",
        limit: 200,
        offset: 0,
      }),
    enabled: !agencyScoped && canListAgencies,
  });
  const agencyOptions = (agenciesResult?.data ?? []).map((a, i) => ({
    value: String(a.id),
    label: agencyDisplayName(a, i),
  }));

  const { page, setPage, pageSize, setPageSize } = useUrlPaging();
  const [filters, setFilters] = useState(null);
  const handleFiltersChange = useCallback(
    (next) => setFilters((prev) => (prev?.key === next.key ? prev : next)),
    []
  );
  const lastFilterKey = useRef(null);
  useEffect(() => {
    if (!filters) return;
    if (lastFilterKey.current && lastFilterKey.current !== filters.key)
      setPage(0);
    lastFilterKey.current = filters.key;
  }, [filters, setPage]);
  const effectiveAgencyId =
    defaultAgencyId ?? (filters?.agency ? Number(filters.agency) : undefined);

  const buildListParams = (limit, offset) => ({
    axiosPrivate,
    ...filters?.apiParams,
    requested_for_agency_id: effectiveAgencyId,
    sprint_plan_assigned:
      view === "assigned" && !agencyScoped ? true : undefined,
    limit,
    offset,
  });

  const { data: result, isLoading } = useQuery({
    queryKey: [
      ...GET_AD_REQUESTS_QUERY_KEY,
      view,
      filters?.key,
      defaultAgencyId,
      agencyScoped,
      page,
      pageSize,
    ],
    queryFn: () =>
      AdsAPIs.listAdRequests(buildListParams(pageSize, page * pageSize)),
    placeholderData: keepPreviousData,
    enabled: !!filters,
  });
  const requests = result?.data ?? [];
  const totalCount = result?.count ?? 0;

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === requests.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(requests.map((r) => r.id)));
  };

  const handleExportExcel = async () => {
    if (exportingExcel) return;
    setExportingExcel(true);
    toast.loading("Preparing export…", { id: "export-excel" });
    try {
      const { rows, total } = await fetchAllFilteredRows({
        fetchPage: ({ limit, offset }) =>
          AdsAPIs.listAdRequests(buildListParams(limit, offset)),
      });
      if (!rows.length) {
        toast.dismiss("export-excel");
        toast.error("No rows to export.");
        return;
      }
      await downloadExcelFromRows({
        filename: exportFilename(
          view === "assigned" ? "ad-requests-assigned" : "ad-requests"
        ),
        sheetName: "Ad requests",
        columns: adRequestExportColumns(showAgencyFilter),
        rows,
      });
      toast.dismiss("export-excel");
      toast.success(`Exported ${total} row(s).`);
    } catch (e) {
      toast.dismiss("export-excel");
      if (e?.message === "EXPORT_TOO_LARGE") {
        toast.error(
          `Too many rows (${e.total}). Max export is ${e.maxRows}. Narrow filters.`
        );
      } else {
        toast.error(e?.message || "Export failed.");
      }
    } finally {
      setExportingExcel(false);
    }
  };

  const handleDeleteSelectedConfirm = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    setDeleteInProgress(true);
    try {
      await AdsAPIs.deleteAdRequestsBulk({ axiosPrivate, ids });
      queryClient.invalidateQueries({ queryKey: GET_AD_REQUESTS_QUERY_KEY });
      toast.success(`${ids.length} ad request(s) deleted.`);
      setSelectedIds(new Set());
      setDeleteConfirmOpen(false);
    } catch (_) {
    } finally {
      setDeleteInProgress(false);
    }
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          borderRadius: "12px",
          border: "1px solid #e5e7eb",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          minHeight: "calc(100vh - 200px)",
        }}
      >
        <div className="p-4 border-b border-gray-200 space-y-4">
          <RequestFilterBar
            agencyScoped={agencyScoped}
            showAgencyFilter={showAgencyFilter}
            agencyOptions={agencyOptions}
            defaultAgencyId={defaultAgencyId}
            onChange={handleFiltersChange}
            actions={
              <RequestListActions
                exporting={exportingExcel}
                disabled={isLoading || !filters}
                onExport={handleExportExcel}
                canMutate={canMutateRequests}
                onDeleteByDate={() => setBulkDeleteByDateOpen(true)}
              />
            }
          />

          {canMutateRequests && selectedIds.size > 0 && (
            <SelectionBar
              count={selectedIds.size}
              onClear={() => setSelectedIds(new Set())}
              onAssignDate={() =>
                setDeliveryDateModal({ open: true, ids: [...selectedIds] })
              }
              onChangeLaunchDate={
                canEditLaunchDate
                  ? () =>
                      setLaunchDateModal({ open: true, ids: [...selectedIds] })
                  : undefined
              }
              onAssignSprint={() =>
                setSprintModal({ open: true, ids: [...selectedIds] })
              }
              onDelete={() => setDeleteConfirmOpen(true)}
            />
          )}
        </div>
        <Box
          sx={{
            flex: "1 1 auto",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          {!result ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              minHeight={320}
              gap={2}
            >
              <CircularProgress size={28} sx={{ color: "#7f7ad5" }} />
              <span className="text-[#7f7ad5] font-medium">
                Loading requests…
              </span>
            </Box>
          ) : (
            <RequestsTable
              requests={requests}
              canMutate={canMutateRequests}
              canEditLaunchDate={canEditLaunchDate}
              showAgencyColumn={showAgencyFilter}
              selectedIds={selectedIds}
              onToggle={toggleSelect}
              onToggleAll={toggleSelectAll}
              onOpen={openDetail}
              onModeration={(r) =>
                setModerationModal({ id: r.id, adsetName: r.adset_name })
              }
              onLaunch={(r) =>
                setLaunchModal({ id: r.id, adsetName: r.adset_name })
              }
              onEdit={setEditRequest}
            />
          )}
          <PaginationFooter
            shown={requests.length}
            totalCount={totalCount}
            page={page}
            onPageChange={setPage}
            pageSize={pageSize}
            onPageSizeChange={(n) => {
              setPageSize(n);
              setPage(0);
            }}
          />
        </Box>
      </Paper>

      {deliveryDateModal.open && (
        <AssignDeliveryDateModal
          open={deliveryDateModal.open}
          onClose={() => setDeliveryDateModal({ open: false })}
          requestIds={deliveryDateModal.ids}
        />
      )}

      {launchDateModal.open && (
        <BulkLaunchDateModal
          open={launchDateModal.open}
          ids={launchDateModal.ids}
          requests={requests}
          onClose={() => setLaunchDateModal({ open: false })}
          onDone={() => {
            setLaunchDateModal({ open: false });
            setSelectedIds(new Set());
          }}
        />
      )}
      {sprintModal.open && (
        <AssignSprintModal
          open={sprintModal.open}
          onClose={() => setSprintModal({ open: false })}
          requestIds={sprintModal.ids}
        />
      )}

      {deleteConfirmOpen && (
        <DeleteConfirmModal
          open={deleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
          count={selectedIds.size}
          onConfirm={handleDeleteSelectedConfirm}
          isLoading={deleteInProgress}
        />
      )}

      {bulkDeleteByDateOpen && (
        <BulkDeleteByDateModal
          open={bulkDeleteByDateOpen}
          onClose={() => setBulkDeleteByDateOpen(false)}
        />
      )}

      <ModerationStatusModal
        open={!!moderationModal}
        onClose={() => setModerationModal(null)}
        requestId={moderationModal?.id}
        adsetName={moderationModal?.adsetName}
      />
      <LaunchStatusModal
        open={!!launchModal}
        onClose={() => setLaunchModal(null)}
        requestId={launchModal?.id}
        adsetName={launchModal?.adsetName}
      />
      <EditAdRequestModal
        open={!!editRequest}
        onClose={() => setEditRequest(null)}
        request={editRequest}
      />
    </>
  );
};

export default AdRequestList;
